import { createContext, FC, ReactNode, useState } from 'react'
import { AlertDialog, Dialog } from '@branch-messenger/willow-ui'
import {
  DialogContextType,
  ShowAlertProps,
  ShowDialogProps,
} from '@customTypes/dialog'
import { fireMixpanelEvent } from '@lib/analytics'

export const DialogContext = createContext<DialogContextType>({
  showAlert: () => null,
  hideAlert: () => null,
  showDialog: () => null,
  hideDialog: () => null,
})

const initialAlertProps: ShowAlertProps = {
  title: '',
  description: '',
  actionDescription: '',
  cancelDescription: '',
  handleAction: () => null,
}

const initialDialogProps: ShowDialogProps = {
  title: '',
  description: '',
  children: null,
}

interface Props {
  children: ReactNode
}

export const DialogProvider: FC<Props> = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [alertProps, setAlertProps] =
    useState<ShowAlertProps>(initialAlertProps)
  const [dialogProps, setDialogProps] =
    useState<ShowDialogProps>(initialDialogProps)

  const showAlert = (props: ShowAlertProps) => {
    hideDialog()
    setAlertProps(props)
    setOpen(true)
    fireMixpanelEvent('general:miscellaneous|alert.pageview', {
      title: props.title,
      description: props.description || '',
    })
  }

  const hideAlert = () => {
    setAlertProps(initialAlertProps)
    setOpen(false)
  }

  const showDialog = (props: ShowDialogProps) => {
    hideAlert()
    setDialogProps(props)
    setDialogOpen(true)
    fireMixpanelEvent('general:miscellaneous|dialog.pageview', {
      title: props.title,
      description: props.description || '',
    })
  }

  const hideDialog = () => {
    setDialogProps(initialDialogProps)
    setDialogOpen(false)
  }

  const dialogContextValues = { showAlert, hideAlert, showDialog, hideDialog }

  return (
    <DialogContext value={dialogContextValues}>
      <AlertDialog
        {...alertProps}
        open={open}
        onOpenChange={hideAlert}
        size="sm"
      />
      <Dialog {...dialogProps} open={dialogOpen} onOpenChange={hideDialog}>
        {dialogProps.children}
      </Dialog>
      {children}
    </DialogContext>
  )
}
